import * as React from "react";

import {
  Field,
  Fieldset,
  Radio,
  RadioGroup,
  TextInput,
} from "@/shared/foundation/primitives";

import { ParameterError } from "./ParameterError";
import styles from "./parameterForms.module.css";
import { useParameterRecord } from "./parameterValues";
import { TextParameter } from "./TextParameter";
import { previewTopicName } from "./topicNameRegex";

const FIXED_KEY = "TOPIC_NAME";
const REGEX_KEY = "TOPIC_NAME_FROM_FILE_NAME_REGEX";

type TopicNameMode = "file-name" | "fixed" | "regex";

export interface TopicNameControlProps {
  name: string;
  /** The file name the regex preview starts on. */
  exampleFileName: string;
  /**
   * Whether the action names the topic after the file when neither key is
   * set. An action that does not fails instead, so the file-name choice is
   * left out and a fixed name is the starting choice.
   */
  fallsBackToFileName: boolean;
}

/**
 * What the ingested topic is called: the file's name, one fixed name, or a
 * part of the file name a regex picks out, previewed against an example.
 */
export function TopicNameControl({
  name,
  exampleFileName,
  fallsBackToFileName,
}: TopicNameControlProps) {
  const record = useParameterRecord(name);
  const legendId = React.useId();
  const regex = record.get(REGEX_KEY);
  let stored: TopicNameMode = fallsBackToFileName ? "file-name" : "fixed";
  if (regex !== undefined) {
    stored = "regex";
  } else if (record.get(FIXED_KEY) !== undefined) {
    stored = "fixed";
  }
  const [picked, setPicked] = React.useState<TopicNameMode | null>(null);
  const mode = picked ?? stored;
  const [example, setExample] = React.useState(exampleFileName);
  const preview =
    regex === undefined ? null : previewTopicName(regex, example.trim());

  return (
    <Fieldset.Root data-testid="param-topic-name">
      <Fieldset.Legend id={legendId}>Topic name</Fieldset.Legend>
      <RadioGroup<TopicNameMode>
        aria-labelledby={legendId}
        value={mode}
        onValueChange={(next) => {
          setPicked(next);
          // Only the chosen way's key is kept.
          record.write({
            ...(next !== "fixed" ? { [FIXED_KEY]: undefined } : {}),
            ...(next !== "regex" ? { [REGEX_KEY]: undefined } : {}),
          });
        }}
      >
        {fallsBackToFileName && (
          <Radio value="file-name" label="From the file name" />
        )}
        <Radio value="fixed" label="Fixed name" />
        <Radio value="regex" label="Extract from the file name" />
      </RadioGroup>

      {mode === "fixed" && (
        <div className={styles.nested}>
          <TextParameter
            name={name}
            parameterKey={FIXED_KEY}
            label="Topic name"
            mono
          />
        </div>
      )}

      {mode === "regex" && (
        <div className={styles.nested}>
          <TextParameter
            name={name}
            parameterKey={REGEX_KEY}
            label="Pattern"
            description="A regular expression with a group named topic_name, in Python syntax: (?P<topic_name>...)."
            placeholder="(?P<topic_name>.+)-log\.csv"
            mono
          />
          <Field.Root>
            <Field.Label>Example file name</Field.Label>
            <Field.Control
              render={
                <TextInput
                  spellCheck={false}
                  size="lg"
                  className={styles.mono}
                />
              }
              value={example}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setExample(event.target.value);
              }}
            />
          </Field.Root>
          {preview !== null && (
            <div
              className={styles.preview}
              data-testid="topic-name-preview"
              data-ok={preview.ok}
              role="status"
            >
              {preview.ok ? (
                <>
                  <span>Topic name:</span>
                  <code className={styles.mono}>{preview.topicName}</code>
                </>
              ) : (
                preview.error
              )}
            </div>
          )}
        </div>
      )}

      {/* A key whose text field this choice hides shows its error here. */}
      {mode !== "fixed" && <ParameterError message={record.error(FIXED_KEY)} />}
      {mode !== "regex" && <ParameterError message={record.error(REGEX_KEY)} />}
    </Fieldset.Root>
  );
}
